import { agruparLineasPorNombre, mostrarUnidadesEnLineas, normalizarTextoLinea } from '@siiv/schemas';
import type { HoverTipCard, HoverTipCardRow } from '../../layout/hoverTipCard';

// Composition only: the rules (name equality, when the unit label shows) are the tested helpers of
// `@siiv/schemas` (`lineas-display.ts`). The ORDER is the researcher's (manual order, per group): the lists arrive
// already ordered by the API and nothing here sorts them. Structural input so the profile, the edit sidebar and the print
// export can all feed it with their own `ResearchLineRecord`.

interface LineaEntrada {
	slug: string;
	name: string;
	/** Owner unit name; used only when the payload carries no unit labels (a stale cached response). */
	unit?: string;
	/** Names of the units that back the line for this researcher (empty for a transversal line). */
	unitLabels?: readonly string[];
	/** 'propia' = a custom (free-text) line; anything else is institutional (a stale payload has no kind). */
	kind?: 'uda' | 'propia';
	/** Catalog areas of an institutional line. Absent on a stale payload and on custom lines. */
	areaOcde?: string | null;
	subareaOcde?: string | null;
	areaSies?: string | null;
}

/** One OCDE area of a line; `subarea` only when the catalog has one. */
export interface AreaOcdeLinea {
	area: string;
	subarea?: string;
}

/** The catalog areas of ONE listed entry: distinct, in first-seen order. Empty for a custom line. */
export interface AreasLinea {
	ocde: AreaOcdeLinea[];
	sies: string[];
}

export interface LineaVista {
	/** Identity of the entry (`normalizarTextoLinea` of the name): a stable React/pending-delete key. */
	clave: string;
	nombre: string;
	/** Units to show next to the name IN THE ROW; empty unless the lines span more than one unit (RLM-03). */
	unidades: string[];
	/**
	 * Every unit that backs the entry (union of the merged lines), whatever the rest of the list looks like;
	 * empty for a transversal or a custom line. The hover tooltip lists them always, unlike the row.
	 */
	unidadesDeLinea: string[];
	/**
	 * Institutional (UDA catalog) lines carry the UDA shield on the left of their row and are listed in the "Líneas de
	 * investigación UDA" section; custom lines carry the flask and go in "Otras líneas de investigación"
	 * (`gruposDeLineas`, CRL-04). The icon is decorative: a screen reader gets the kind from the row's hidden text
	 * (`tipoDeLineaOculto`).
	 */
	institucional: boolean;
	/** OCDE and SIES areas of the line (the tooltip and the hidden text of the row); empty for custom lines. */
	areas: AreasLinea;
}

export interface ListaLineas {
	items: LineaVista[];
	mostrarUnidad: boolean;
}

const etiquetasDe = (linea: LineaEntrada): string[] => {
	if (linea.unitLabels && linea.unitLabels.length > 0) return [...linea.unitLabels];
	const owner = linea.unit?.trim();
	return owner ? [owner] : [];
};

const esPropia = (linea: { kind?: 'uda' | 'propia' }) => linea.kind === 'propia';

const SIN_AREAS = (): AreasLinea => ({ ocde: [], sies: [] });

/**
 * Unions the areas of every institutional line listed under one name (distinct, ignoring case and accents,
 * first-seen order). A blank area counts as no area. The same OCDE area with and without subarea are two
 * entries: the subarea is part of what is told.
 */
function unirAreas(lineas: readonly LineaEntrada[]): AreasLinea {
	const areas = SIN_AREAS();
	const vistasOcde = new Set<string>();
	const vistasSies = new Set<string>();
	for (const linea of lineas) {
		const area = linea.areaOcde?.trim() ?? '';
		const subarea = linea.subareaOcde?.trim() ?? '';
		if (area || subarea) {
			const clave = `${normalizarTextoLinea(area)}::${normalizarTextoLinea(subarea)}`;
			if (!vistasOcde.has(clave)) {
				vistasOcde.add(clave);
				areas.ocde.push(subarea ? { area, subarea } : { area });
			}
		}
		const sies = linea.areaSies?.trim() ?? '';
		if (sies) {
			const clave = normalizarTextoLinea(sies);
			if (!vistasSies.has(clave)) {
				vistasSies.add(clave);
				areas.sies.push(sies);
			}
		}
	}
	return areas;
}

/**
 * The single list of lines of a researcher. Institutional lines come first: de-duplicated by name
 * (presentational: the underlying assignments stay separate) and, only when THEY span more than one unit,
 * every entry carries the units that back it. A single-unit profile renders exactly as it did before.
 * Custom (free-text) lines follow in the order saved: no unit label, no de-duplication against institutional lines,
 * and they never count toward "span more than one unit" (CRL-04, CRL-05). `institucional` tells the two kinds apart;
 * the profile sidebars (public, owner view, edit) and the print export split the list in two sections with
 * `gruposDeLineas`.
 */
export function construirListaLineas(lineas: readonly LineaEntrada[]): ListaLineas {
	const institucionales = lineas.filter((linea) => !esPropia(linea));
	const personalizadas = lineas.filter(esPropia);
	const grupos = agruparLineasPorNombre(
		institucionales.map((linea) => ({ nombre: linea.name, unidades: etiquetasDe(linea) })),
	);
	const mostrarUnidad = mostrarUnidadesEnLineas(grupos);
	const lineasDelGrupo = new Map<string, LineaEntrada[]>();
	for (const linea of institucionales) {
		const clave = normalizarTextoLinea(linea.name);
		lineasDelGrupo.set(clave, [...(lineasDelGrupo.get(clave) ?? []), linea]);
	}
	return {
		mostrarUnidad,
		items: [
			...grupos.map(
				(grupo): LineaVista => ({
					clave: grupo.clave,
					nombre: grupo.nombre,
					unidades: mostrarUnidad ? grupo.unidades : [],
					unidadesDeLinea: grupo.unidades,
					institucional: true,
					areas: unirAreas(lineasDelGrupo.get(grupo.clave) ?? []),
				}),
			),
			...personalizadas.map(
				(linea, indice): LineaVista => ({
					// The prefix keeps it apart from an institutional line that has the same name.
					clave: `propia:${indice}`,
					nombre: linea.name.trim(),
					unidades: [],
					unidadesDeLinea: [],
					institucional: false,
					areas: SIN_AREAS(),
				}),
			),
		],
	};
}

/** Title of the section with the institutional (UDA catalogue) lines. */
export const TITULO_LINEAS_UDA = 'Líneas de investigación UDA';

/** Title of the section with the custom (free-text) lines. */
export const TITULO_OTRAS_LINEAS = 'Otras líneas de investigación';

/** One lines section: its title and its rows, in their saved order. */
export interface GrupoLineas {
	clave: 'uda' | 'externas';
	titulo: string;
	items: LineaVista[];
}

/**
 * The one list split in the two sections of the sidebars and the print export: the institutional lines under
 * "Líneas de investigación UDA" first, then the custom ones under "Otras líneas de investigación", each keeping the
 * order `construirListaLineas` gave them. A section with no lines is left out (callers that always draw the UDA box,
 * like the edit sidebar, look it up by `clave`).
 */
export function gruposDeLineas(items: readonly LineaVista[]): GrupoLineas[] {
	const grupos: GrupoLineas[] = [
		{ clave: 'uda', titulo: TITULO_LINEAS_UDA, items: items.filter((item) => item.institucional) },
		{ clave: 'externas', titulo: TITULO_OTRAS_LINEAS, items: items.filter((item) => !item.institucional) },
	];
	return grupos.filter((grupo) => grupo.items.length > 0);
}

/** Hidden sentence of an institutional (UDA catalogue) line row: the kind the shield icon shows visually. */
export const TEXTO_OCULTO_LINEA_INSTITUCIONAL = 'Línea institucional UDA.';

/** Hidden sentence of a custom (free-text) line row, matching its flask icon. */
export const TEXTO_OCULTO_LINEA_PROPIA = 'Línea externa.';

/**
 * What a screen reader (and a crawler) is told about the kind of a line. The row icon is `aria-hidden` and the edit
 * sidebar draws rows outside their section title, so each row carries its kind as `sr-only` text.
 */
export function tipoDeLineaOculto(institucional: boolean): string {
	return institucional ? TEXTO_OCULTO_LINEA_INSTITUCIONAL : TEXTO_OCULTO_LINEA_PROPIA;
}

/** One phrase per area: "Área OCDE: X (subárea: Y)" first, then "Área SIES: Z". Empty when the line has none. */
export function frasesDeAreas(areas: AreasLinea): string[] {
	return [
		...areas.ocde.map(({ area, subarea }) => {
			if (!area) return `Subárea OCDE: ${subarea ?? ''}`;
			return subarea ? `Área OCDE: ${area} (subárea: ${subarea})` : `Área OCDE: ${area}`;
		}),
		...areas.sies.map((area) => `Área SIES: ${area}`),
	];
}

/** "Unidad"/"Unidades", "Área OCDE"/"Áreas OCDE": the label agrees with how many values the row lists. */
const etiquetaDeFila = (singular: string, plural: string, cuenta: number) => (cuenta > 1 ? plural : singular);

/**
 * The hover tooltip of a row as a card (`HoverTip`): a header with the icon and the full name, then one
 * labelled row per fact the line has, in this order: the unit(s), the OCDE area (its subarea beneath it) and the
 * SIES area. A row without a value is left out. Unit names stay on ONE line (`unaLinea`); the line's own name
 * (the title) may wrap. The unit is ALWAYS listed for an institutional line — unlike the
 * row, which only shows it when the lines span several units. A custom line is its name plus a
 * "Línea personalizada" caption: it has no unit and no catalog areas.
 */
export function tarjetaDeLinea(item: {
	nombre: string;
	institucional: boolean;
	unidadesDeLinea: readonly string[];
	areas: AreasLinea;
}): HoverTipCard {
	if (!item.institucional) {
		return { icon: 'flask', title: item.nombre, caption: 'Línea personalizada', rows: [] };
	}

	const unidades = [...new Set(item.unidadesDeLinea.map((unidad) => unidad.trim()).filter(Boolean))];
	const ocde = item.areas.ocde.flatMap(({ area, subarea }) => {
		if (area) return [subarea ? { text: area, detail: subarea } : { text: area }];
		return subarea ? [{ text: `Subárea: ${subarea}` }] : [];
	});
	const sies = item.areas.sies.map((area) => ({ text: area }));

	const filas: HoverTipCardRow[] = [
		{ icon: 'building', label: etiquetaDeFila('Unidad', 'Unidades', unidades.length), values: unidades.map((text) => ({ text, unaLinea: true })) },
		{ icon: 'globe', label: etiquetaDeFila('Área OCDE', 'Áreas OCDE', ocde.length), values: ocde },
		{ icon: 'book', label: etiquetaDeFila('Área SIES', 'Áreas SIES', sies.length), values: sies },
	];

	// Institutional line: the card header carries the University icon; custom lines keep the flask.
	return { icon: 'escudo', title: item.nombre, rows: filas.filter((fila) => fila.values.length > 0) };
}

/**
 * The areas as plain text for the row's `sr-only` copy: the tooltip only exists while hovering, and a
 * crawler or a screen reader never hovers (SEO rule). Empty when the line has no areas.
 */
export function textoOcultoDeAreas(areas: AreasLinea): string {
	return frasesDeAreas(areas)
		.map((frase) => `${frase}.`)
		.join(' ');
}

/**
 * The whole `sr-only` copy of a row: the kind of line ("Línea institucional UDA." / "Línea externa."), then the units
 * the row used to show as a visible sub-label (only when the lines span several units, RLM-03) and the areas. The row
 * is a single line and the tooltip only exists while hovering or focusing, so this text is what keeps the kind, the
 * units and the areas in the served HTML and in front of a screen reader (SEO rule). It is never empty: every row
 * has a kind.
 */
export function textoOcultoDeLinea(item: {
	institucional: boolean;
	unidades: readonly string[];
	areas: AreasLinea;
}): string {
	const unidades = item.unidades.map((unidad) => unidad.trim()).filter(Boolean);
	const frase = unidades.length > 0 ? `${unidades.length > 1 ? 'Unidades' : 'Unidad'}: ${unidades.join(', ')}.` : '';
	return [tipoDeLineaOculto(item.institucional), frase, textoOcultoDeAreas(item.areas)].filter(Boolean).join(' ');
}

/**
 * The first `limite` DISTINCT institutional line names, for the meta description (RLM-05). Deduplication happens BEFORE
 * taking the first ones, so a repeated name never uses up a slot.
 */
export function nombresDistintosDeLineas(
	lineas: readonly { name: string; kind?: 'uda' | 'propia' }[],
	limite: number,
): string[] {
	const vistos = new Set<string>();
	const nombres: string[] = [];
	for (const linea of lineas) {
		// The description names institutional lines only (RLM-05).
		if (esPropia(linea)) continue;
		const clave = normalizarTextoLinea(linea.name ?? '');
		if (!clave || vistos.has(clave)) continue;
		vistos.add(clave);
		nombres.push(linea.name.trim());
		if (nombres.length === limite) break;
	}
	return nombres;
}
